import { bool, object, string } from "yup";

import type { ManagedLocation } from "@thalamos/aspire-common";
import {
  formValidationErrorMessages,
  nonEmptyString,
  postalCodeValidationRule,
} from "@thalamos/aspire-common";

/**
 * The kinds of location the managed-location forms accept;
 * `managedLocationSchema` refuses any other, so other kinds are kept out of the
 * listing rather than offering an edit that cannot be saved.
 */
export const EDITABLE_LOCATION_TYPES = [
  "hospital",
  "localAuthority",
  "careHome",
] as const;

export type EditableLocationType = (typeof EDITABLE_LOCATION_TYPES)[number];

export const isEditableLocation = (location: ManagedLocation): boolean =>
  (EDITABLE_LOCATION_TYPES as readonly string[]).includes(location.type);

export const managedLocationSchema = object({
  type: string()
    .default(null)
    .oneOf([...EDITABLE_LOCATION_TYPES])
    .nonNullable("Type is required"),

  typeLabel: string().default(""),

  name: nonEmptyString
    .default("")
    .required("Please enter a name")
    .max(255, formValidationErrorMessages.tooManyCharactersError),

  address: object({
    address: nonEmptyString
      .required("Address is required")
      .max(255, formValidationErrorMessages.tooManyCharactersError),
    postalCode: postalCodeValidationRule("type", "hospital"),
    isConfirmed: bool().required().isTrue("Please confirm your address"),
  }).default({
    address: "",
    isConfirmed: false,
    postalCode: "",
  }),

  odsCode: string().nullable().default(null).optional(),

  trustId: string()
    .uuid("Invalid Trust ID")
    .nullable()
    .default(null)
    .optional(),

  xeroInvoicePrefix: string()
    .nullable()
    .default(null)
    .optional()
    .max(30, "Shortcode must be 30 characters or fewer"),

  // Care homes are registered with the CQC rather than carrying an ODS code.
  cqcLocationId: string()
    .nullable()
    .default(null)
    .optional()
    .max(255, formValidationErrorMessages.tooManyCharactersError),

  // The body that grants DoLS authorisations for this home's residents. A
  // per-home fact, which is why it lives on the location and not the resident.
  supervisoryLocalAuthorityId: string()
    .uuid("Invalid local authority")
    .nullable()
    .default(null)
    .optional(),

  // The board this authority sits under; meaningful on local-authority rows
  // alone.
  icbId: string().uuid("Invalid ICB").nullable().default(null).optional(),
});

export type ManagedLocationFormData = {
  type: EditableLocationType | null;

  typeLabel: string;
  name: string;
  address: {
    address: string;
    postalCode: string;
    isConfirmed: boolean;
  };
  odsCode: string | null;
  // TODO MAKE REQUIRED (DEV-1140)
  trustId?: string | null;
  xeroInvoicePrefix?: string | null;
  cqcLocationId?: string | null;
  supervisoryLocalAuthorityId?: string | null;
  icbId?: string | null;
};
